/**
 * Comparison Section Component
 * Shows earnings comparison between trips and wage slips
 */
import React from 'react';
import { AlertCircle } from 'lucide-react';

const ComparisonSection = ({ comparison }) => {
  if (!comparison || comparison.wage_slip_earnings === null) {
    return null;
  }

  return (
    <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
      <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Gross Earnings Comparison</p>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-slate-500">Gross Pay (from Trips)</p>
          <p className="text-lg font-bold text-amber-400" data-testid="comparison-trip-gross">
            £{comparison.trip_earnings?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500">Gross Pay (from Wage Slips)</p>
          <p className="text-lg font-bold text-blue-400" data-testid="comparison-wageslip-gross">
            £{comparison.wage_slip_earnings?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
          </p>
        </div>
      </div>
      
      {comparison.difference !== null && comparison.difference !== 0 && (
        <div className={`flex items-center gap-2 text-sm ${Math.abs(comparison.difference) < 1 ? 'text-amber-400' : Math.abs(comparison.difference) < comparison.trip_earnings * 0.05 ? 'text-amber-400' : 'text-red-400'}`}>
          {comparison.difference > 0 ? (
            <>Gross trip pay £{Math.abs(comparison.difference).toFixed(2)} higher</>
          ) : (
            <>Gross wage slip £{Math.abs(comparison.difference).toFixed(2)} higher</>
          )}
        </div>
      )}
      
      {/* Discrepancy Notice */}
      {comparison.difference !== null && comparison.difference > 0 && Math.abs(comparison.difference) >= 1 && (
        <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <p className="text-xs text-amber-400 font-medium flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Discrepancy Notice
          </p>
          <p className="text-xs text-slate-400 mt-1">
            This comparison uses gross pay figures only. If your gross wage slip earnings differ from calculated gross trip pay, check that trip details (day rates, uplifts, and dates) match your actual contract. 
            Common reasons for differences include: travel days at reduced rates, days without offshore uplift, or different rate periods.
          </p>
        </div>
      )}
      
      {/* Extracted deductions */}
      {(comparison.extracted_tax_total || comparison.extracted_pension_total) && (
        <div className="pt-2 border-t border-slate-700 grid grid-cols-2 gap-4 text-sm">
          {comparison.extracted_tax_total && (
            <div>
              <p className="text-xs text-slate-500">Income Tax (from slip)</p>
              <p className="font-medium text-orange-400">£{comparison.extracted_tax_total?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
            </div>
          )}
          {comparison.extracted_pension_total && (
            <div>
              <p className="text-xs text-slate-500">Pension (from slip)</p>
              <p className="font-medium text-purple-400">£{comparison.extracted_pension_total?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ComparisonSection;
